import { Injectable, inject } from '@angular/core';
import { Observable, finalize, shareReplay } from 'rxjs';
import { FileManagerDriver } from './file-manager-driver.interface';
import { VueFinderDriver } from './vue-finder-driver.service';
import type {
    FsData,
    DeleteResult,
    FileOperationResult,
    SearchResult,
    QuotaResponse,
} from '../models/api';

@Injectable({
    providedIn: 'root',
})
export class FileManagerService {
    private driver: FileManagerDriver = inject(VueFinderDriver);

    private readonly quotaInFlight = new Map<string, Observable<QuotaResponse>>();
    private readonly listInFlight = new Map<string, Observable<FsData>>();

    setDriver(driver: FileManagerDriver) {
        this.driver = driver;
    }

    listFiles(path: string, options?: { skipErrorToast?: boolean }): Observable<FsData> {
        const key = `${path}|${options?.skipErrorToast ? 1 : 0}`;
        const existing = this.listInFlight.get(key);
        if (existing) return existing;

        const request = this.driver.listFiles(path, options).pipe(
            finalize(() => this.listInFlight.delete(key)),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
        this.listInFlight.set(key, request);
        return request;
    }

    uploadFiles(path: string, files: File[]): Observable<{ progress: number; data?: any }> {
        return this.driver.uploadFiles(path, files);
    }

    createFolder(path: string, name: string): Observable<FileOperationResult> {
        return this.driver.createFolder(path, name);
    }

    createFile(path: string, name: string): Observable<FileOperationResult> {
        return this.driver.createFile(path, name);
    }

    deleteFiles(path: string, items: Array<{ path: string; type: 'file' | 'dir' }>): Observable<DeleteResult> {
        return this.driver.deleteFiles(path, items);
    }

    searchFiles(path: string, filter?: string, deep?: boolean, size?: 'all' | 'small' | 'medium' | 'large'): Observable<SearchResult> {
        return this.driver.searchFiles(path, filter, deep, size);
    }

    renameFile(path: string, item: string, name: string): Observable<FileOperationResult> {
        return this.driver.renameFile(path, item, name);
    }

    moveFiles(fromSource: string, sources: string[], toSource: string): Observable<FileOperationResult> {
        return this.driver.moveFiles(fromSource, sources, toSource);
    }

    getPreviewUrl(path: string): string {
        return this.driver.getPreviewUrl(path);
    }

    getDownloadUrl(path: string): string {
        return this.driver.getDownloadUrl(path);
    }

    getFileSystemQuota(storage: string): Observable<QuotaResponse> {
        const existing = this.quotaInFlight.get(storage);
        if (existing) return existing;

        const request = this.driver.getFileSystemQuota(storage).pipe(
            finalize(() => this.quotaInFlight.delete(storage)),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
        this.quotaInFlight.set(storage, request);
        return request;
    }
}
